import { Classes, Dialog } from "@blueprintjs/core";
import { FieldGroup } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import * as Yup from "yup";

import {
  LieuFormationFichierApi,
  LieuFormationFichierUploadDto,
  LieuFormationFichierUploadDtoFromJSON
} from "../../../../../api";
import { FGWalterFileInput, FGWalterSelectInput, SmallFormGenerator } from "../../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

const StyledDialog = styled(Dialog)`
  width: 600px;
  height: auto;
  background-color: white;
`;

export interface ILieuFormationDocumentUploadDialogProps {
  idlieuFormation: number;
  dialogOpen: boolean;
  onClose: (refresh?: boolean) => void;
}

export const LieuFormationDocumentUploadDialog: React.FunctionComponent<ILieuFormationDocumentUploadDialogProps> = ({
  idlieuFormation,
  dialogOpen,
  onClose
}) => {
  const { t } = useTl();
  const api = useApiService(LieuFormationFichierApi);

  const { data, loading, saveItem, saving, validationErrors } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: () => {
          return LieuFormationFichierUploadDtoFromJSON({ idlieuFormation });
        },
        saveApiFn: async (d: LieuFormationFichierUploadDto) => {
          return api.lieuFormationFichierUploadLieuFormationFichier({ LieuFormationFichierUploadDto: d });
        },
        onSaved: () => onClose(true)
      }),
      [api, idlieuFormation, onClose]
    )
  );

  const FormSchema = React.useMemo(
    () =>
      Yup.object().shape({
        idtypeLieuFormationFichier: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required)),
        file: Yup.object()
          .nullable()
          .shape({
            fileName: Yup.string()
              .nullable()
              .required(t(ETLCodes.Required)),
            contentB64: Yup.string()
              .nullable()
              .required(t(ETLCodes.Required))
          })
      }),
    [t]
  );

  const [typesLieuFormationFichier, tLoading] = useReferential(a => a.referentialGetTypesLieuFormationFichier());

  return (
    <StyledDialog title={t(ETLCodes.UploadFichier)} isOpen={dialogOpen} onClose={() => onClose(false)}>
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={data}
          onSubmit={saveItem}
          editMode={true}
          validationSchema={FormSchema}
          onCancel={() => onClose(false)}
          showDeleteButton={false}
          saving={saving}
          loading={loading}
          validationErrors={validationErrors}
        >
          <FieldGroup>
            <FGWalterFileInput name="file" label={t(ETLCodes.Fichier)} requiredMark />
            <FGWalterSelectInput
              name="idtypeLieuFormationFichier"
              label={t(ETLCodes.Type)}
              items={typesLieuFormationFichier}
              loading={tLoading}
            />
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </StyledDialog>
  );
};
